import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Listing } from '../types';
import { fetchListingDirect } from '../services/idxBroker';
import Loading from '../components/Loading';

// Fix for default marker icon in Leaflet with webpack
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Generate URL-friendly slug from address
const generateAddressSlug = (address: string): string => {
  return address
    .replace(/[^a-zA-Z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
};

const PropertyDetail: React.FC = () => {
  const { idxID, listingID, slug } = useParams<{ idxID: string; listingID: string; slug?: string }>();
  const navigate = useNavigate();
  const [listing, setListing] = useState<Listing | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);
  const [mapType, setMapType] = useState<'map' | 'satellite'>('map');
  const sidebarRef = useRef<HTMLDivElement>(null);

  // Calculate sticky top position to center sidebar vertically below navbar
  useEffect(() => {
    const updateSidebarPosition = () => {
      const sidebar = sidebarRef.current;
      if (!sidebar) return;

      const navbarHeight = 80;
      const sidebarHeight = sidebar.offsetHeight;
      const viewportHeight = window.innerHeight;
      const availableHeight = viewportHeight - navbarHeight;
      // Center in the space below the navbar
      const centeredTop = navbarHeight + (availableHeight - sidebarHeight) / 2;
      // Don't go above navbar + some padding
      sidebar.style.top = `${Math.max(navbarHeight + 20, centeredTop)}px`;
    };

    updateSidebarPosition();
    window.addEventListener('resize', updateSidebarPosition);
    return () => window.removeEventListener('resize', updateSidebarPosition);
  }, [listing]);

  useEffect(() => {
    async function loadListing() {
      if (!idxID || !listingID) {
        setError('Invalid listing ID');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      const result = await fetchListingDirect(idxID, listingID);

      if (result) {
        setListing(result);

        // Redirect to canonical URL with address slug if missing or incorrect
        const expectedSlug = generateAddressSlug(result.address);
        if (slug !== expectedSlug) {
          navigate(`/listings/${idxID}/${listingID}/${expectedSlug}`, { replace: true });
        }
      } else {
        setError('Listing not found');
      }
      setIsLoading(false);
    }

    loadListing();
  }, [idxID, listingID, slug, navigate]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(price);
  };

  const formatBaths = (baths: number) => {
    if (baths % 1 === 0) return baths;
    return baths.toFixed(1);
  };

  // Get display text for non-active statuses (case-insensitive)
  const getStatusBadge = () => {
    if (!listing) return null;
    const statusLower = listing.status.toLowerCase();
    if (statusLower === 'active') return null;
    if (statusLower === 'sold') return 'Sold';
    if (statusLower === 'pending') {
      // Check propStatus for more specific display text
      const propStatusLower = listing.propStatus?.toLowerCase();
      if (propStatusLower === 'contingent') return 'Contingent';
      return 'Pending';
    }
    return listing.status.charAt(0).toUpperCase() + listing.status.slice(1).toLowerCase();
  };

  const nextImage = () => {
    if (listing?.images) {
      setSelectedImageIndex((prev) => (prev + 1) % listing.images.length);
    }
  };

  const prevImage = () => {
    if (listing?.images) {
      setSelectedImageIndex((prev) => (prev - 1 + listing.images.length) % listing.images.length);
    }
  };

  // Handle keyboard navigation in lightbox
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!showLightbox) return;
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'Escape') setShowLightbox(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showLightbox, listing]);

  // Lock body scroll when lightbox is open
  useEffect(() => {
    if (showLightbox) {
      document.body.classList.add('lightbox-open');
    } else {
      document.body.classList.remove('lightbox-open');
    }
    return () => {
      document.body.classList.remove('lightbox-open');
    };
  }, [showLightbox]);

  // Update document title and meta tags when listing loads
  useEffect(() => {
    if (listing) {
      const listingImages = listing.images?.filter(img => img.url && img.url.length > 0) || [];
      const ogImage = listingImages.length > 0 ? listingImages[0].url : 'https://keithklinegroup.com/og-image.jpg';
      const description = `${listing.beds} bed, ${listing.baths} bath property at ${listing.address}, ${listing.city}, ${listing.state}. ${formatPrice(listing.price)}`;
      const title = `${listing.address} | Kline Real Estate Works`;
      const ogTitle = `${listing.address}, ${listing.city}, ${listing.state} | Kline Real Estate Works`;
      const url = `https://keithklinegroup.com/listings/${idxID}/${listingID}/${generateAddressSlug(listing.address)}`;

      document.title = title;

      // Update or create meta tags
      const updateMeta = (property: string, content: string, isProperty = true) => {
        const attr = isProperty ? 'property' : 'name';
        let meta = document.querySelector(`meta[${attr}="${property}"]`) as HTMLMetaElement;
        if (!meta) {
          meta = document.createElement('meta');
          meta.setAttribute(attr, property);
          document.head.appendChild(meta);
        }
        meta.setAttribute('content', content);
      };

      updateMeta('description', description, false);
      updateMeta('og:type', 'website');
      updateMeta('og:url', url);
      updateMeta('og:title', ogTitle);
      updateMeta('og:description', description);
      updateMeta('og:image', ogImage);
      updateMeta('og:site_name', 'Kline Real Estate Works');
      updateMeta('twitter:card', 'summary_large_image', false);
    }
    return () => {
      document.title = 'Kline Real Estate Works | Royersford, PA Real Estate';
    };
  }, [listing, idxID, listingID]);

  if (isLoading) {
    return (
      <div className="page-content">
        {/* Back Navigation */}
        <div className="container" style={{ paddingTop: '2rem', paddingBottom: '2rem' }}>
          <Link to="/listings" style={{ display: 'inline-flex', alignItems: 'center', color: 'var(--color-navy)', fontWeight: 500 }}>
            <span className="material-icons" style={{ fontSize: '1.25rem', marginRight: '0.25rem' }}>arrow_back</span>
            Back to Listings
          </Link>
        </div>

        {/* Photo Gallery Skeleton */}
        <section className="section" style={{ paddingTop: 0 }}>
          <div className="container">
            <div className="property-gallery">
              <div className="skeleton-gallery">
                <img src="/loading.svg" alt="" className="loading-spinner" />
              </div>
              <div className="skeleton-thumbnails">
                <div className="skeleton skeleton-thumbnail"></div>
                <div className="skeleton skeleton-thumbnail"></div>
                <div className="skeleton skeleton-thumbnail"></div>
                <div className="skeleton skeleton-thumbnail"></div>
                <div className="skeleton skeleton-thumbnail"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Property Header Skeleton */}
        <section className="section" style={{ paddingTop: 0, paddingBottom: '2rem' }}>
          <div className="container">
            <div className="property-header">
              <div className="property-header-main">
                <div className="skeleton skeleton-text" style={{ width: '200px', height: '40px', marginBottom: '1rem' }}></div>
                <div className="skeleton skeleton-text" style={{ width: '300px', height: '24px', marginBottom: '0.5rem' }}></div>
                <div className="skeleton skeleton-text" style={{ width: '200px', height: '18px' }}></div>
              </div>
              <div className="property-quick-stats">
                <div className="skeleton-stat">
                  <div className="skeleton skeleton-text" style={{ width: '40px', height: '32px', marginBottom: '0.25rem' }}></div>
                  <div className="skeleton skeleton-text" style={{ width: '50px', height: '14px' }}></div>
                </div>
                <div className="skeleton-stat">
                  <div className="skeleton skeleton-text" style={{ width: '40px', height: '32px', marginBottom: '0.25rem' }}></div>
                  <div className="skeleton skeleton-text" style={{ width: '50px', height: '14px' }}></div>
                </div>
                <div className="skeleton-stat">
                  <div className="skeleton skeleton-text" style={{ width: '60px', height: '32px', marginBottom: '0.25rem' }}></div>
                  <div className="skeleton skeleton-text" style={{ width: '50px', height: '14px' }}></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Property Details Skeleton */}
        <section className="section section-bg">
          <div className="container">
            <div className="property-details-grid">
              <div className="property-main-content">
                <div className="property-section">
                  <div className="skeleton skeleton-text" style={{ width: '180px', height: '24px', marginBottom: '1rem' }}></div>
                  <div className="skeleton skeleton-text" style={{ width: '100%', height: '16px', marginBottom: '0.5rem' }}></div>
                  <div className="skeleton skeleton-text" style={{ width: '100%', height: '16px', marginBottom: '0.5rem' }}></div>
                  <div className="skeleton skeleton-text" style={{ width: '80%', height: '16px' }}></div>
                </div>
              </div>
              <div className="property-sidebar">
                <div className="skeleton skeleton-card" style={{ height: '200px' }}></div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (error || !listing) {
    return (
      <div className="page-content">
        <div className="section">
          <div className="container" style={{ textAlign: 'center', padding: '4rem 0' }}>
            <span className="material-icons" style={{ fontSize: '4rem', color: 'var(--color-gray-400)', marginBottom: '1rem', display: 'block' }}>
              home
            </span>
            <h2>Property Not Found</h2>
            <p style={{ color: 'var(--color-gray-600)', marginBottom: '2rem' }}>
              {error || 'The property you are looking for could not be found.'}
            </p>
            <Link to="/listings" className="btn btn-primary">View All Listings</Link>
          </div>
        </div>
      </div>
    );
  }

  const images = listing.images?.filter(img => img.url && img.url.length > 0) || [];
  const hasImages = images.length > 0;
  const hasMap = listing.latitude && listing.longitude;
  const totalSlides = images.length + (hasMap ? 1 : 0);
  // If no images, default to showing the map
  const isMapSelected = hasMap && (selectedImageIndex === images.length || !hasImages);

  return (
    <div className="page-content">
      {/* Lightbox Modal - only for images, not map */}
      {showLightbox && !isMapSelected && images.length > 0 && (
        <div className="lightbox-overlay" onClick={() => setShowLightbox(false)}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={() => setShowLightbox(false)}>
              <span className="material-icons">close</span>
            </button>
            <button className="lightbox-nav lightbox-prev" onClick={prevImage}>
              <span className="material-icons">chevron_left</span>
            </button>
            <img src={images[selectedImageIndex]?.url} alt={`Photo ${selectedImageIndex + 1}`} />
            <button className="lightbox-nav lightbox-next" onClick={nextImage}>
              <span className="material-icons">chevron_right</span>
            </button>
            <div className="lightbox-counter">
              {selectedImageIndex + 1} / {images.length}
            </div>
          </div>
        </div>
      )}

      {/* Back Navigation */}
      <div className="container" style={{ paddingTop: '2rem', paddingBottom: '2rem' }}>
        <Link to="/listings" style={{ display: 'inline-flex', alignItems: 'center', color: 'var(--color-navy)', fontWeight: 500 }}>
          <span className="material-icons" style={{ fontSize: '1.25rem', marginRight: '0.25rem' }}>arrow_back</span>
          Back to Listings
        </Link>
      </div>

      {/* Photo Gallery */}
      <section className="section" style={{ paddingTop: 0 }}>
        <div className="container">
          <div className="property-gallery">
            {/* Main Image or Map */}
            <div className={`gallery-main ${isMapSelected ? 'gallery-main-map' : ''}`}>
              {isMapSelected ? (
                <>
                  <div className="gallery-map-container">
                    <div className="map-type-toggle gallery-map-toggle">
                      <button
                        className={`map-type-btn ${mapType === 'map' ? 'active' : ''}`}
                        onClick={() => setMapType('map')}
                      >
                        Map
                      </button>
                      <button
                        className={`map-type-btn ${mapType === 'satellite' ? 'active' : ''}`}
                        onClick={() => setMapType('satellite')}
                      >
                        Satellite
                      </button>
                    </div>
                    <MapContainer
                      key={mapType}
                      center={[listing.latitude!, listing.longitude!]}
                      zoom={15}
                      scrollWheelZoom={true}
                      style={{ height: '100%', width: '100%' }}
                    >
                      {mapType === 'map' ? (
                        <TileLayer
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                      ) : (
                        <TileLayer
                          attribution='Tiles &copy; Esri'
                          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                        />
                      )}
                      <Marker position={[listing.latitude!, listing.longitude!]}>
                        <Popup>
                          <strong>{listing.address}</strong><br />
                          {listing.city}, {listing.state} {listing.zip}
                        </Popup>
                      </Marker>
                    </MapContainer>
                  </div>
                  {getStatusBadge() && (
                    <span className="property-status-badge">{getStatusBadge()}</span>
                  )}
                </>
              ) : images.length > 0 ? (
                <>
                  <img
                    src={images[selectedImageIndex]?.url}
                    alt={listing.address}
                    onClick={() => setShowLightbox(true)}
                    style={{ cursor: 'pointer' }}
                  />
                  <div className="gallery-overlay" onClick={() => setShowLightbox(true)}>
                    <span className="material-icons">fullscreen</span>
                    <span>View Photos ({images.length})</span>
                  </div>
                  {getStatusBadge() && (
                    <span className="property-status-badge">{getStatusBadge()}</span>
                  )}
                </>
              ) : hasMap ? (
                <>
                  <div className="gallery-map-container">
                    <div className="map-type-toggle gallery-map-toggle">
                      <button
                        className={`map-type-btn ${mapType === 'map' ? 'active' : ''}`}
                        onClick={() => setMapType('map')}
                      >
                        Map
                      </button>
                      <button
                        className={`map-type-btn ${mapType === 'satellite' ? 'active' : ''}`}
                        onClick={() => setMapType('satellite')}
                      >
                        Satellite
                      </button>
                    </div>
                    <MapContainer
                      key={mapType}
                      center={[listing.latitude!, listing.longitude!]}
                      zoom={15}
                      scrollWheelZoom={true}
                      style={{ height: '100%', width: '100%' }}
                    >
                      {mapType === 'map' ? (
                        <TileLayer
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                      ) : (
                        <TileLayer
                          attribution='Tiles &copy; Esri'
                          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                        />
                      )}
                      <Marker position={[listing.latitude!, listing.longitude!]}>
                        <Popup>
                          <strong>{listing.address}</strong><br />
                          {listing.city}, {listing.state} {listing.zip}
                        </Popup>
                      </Marker>
                    </MapContainer>
                  </div>
                  {getStatusBadge() && (
                    <span className="property-status-badge">{getStatusBadge()}</span>
                  )}
                </>
              ) : (
                <div className="gallery-no-images">
                  <span className="material-icons" style={{ fontSize: '4rem', color: 'var(--color-gray-400)', marginBottom: '1rem' }}>photo_camera</span>
                  <p style={{ color: 'var(--color-gray-500)' }}>No images available for this property</p>
                </div>
              )}
            </div>

            {/* Thumbnail Strip - only show if we have images or map */}
            {(hasImages || hasMap) && totalSlides > 1 && (
              <div className="gallery-thumbnails">
                {images.map((img, index) => (
                  <div
                    key={index}
                    className={`gallery-thumbnail ${index === selectedImageIndex ? 'active' : ''}`}
                    onClick={() => setSelectedImageIndex(index)}
                  >
                    <img src={img.url} alt={`Photo ${index + 1}`} />
                  </div>
                ))}
                {hasMap && (
                  <div
                    className={`gallery-thumbnail gallery-thumbnail-map ${isMapSelected ? 'active' : ''}`}
                    onClick={() => setSelectedImageIndex(images.length)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                    </svg>
                    <span>Map</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Property Header Info */}
      <section className="section" style={{ paddingTop: 0, paddingBottom: '2rem' }}>
        <div className="container">
          <div className="property-header">
            <div className="property-header-main">
              <h1 className="property-price">{formatPrice(listing.price)}</h1>
              <h2 className="property-address">
                {listing.address}
                <span className="property-location">{listing.city}, {listing.state} {listing.zip}</span>
              </h2>
            </div>
            <div className="property-quick-stats">
              {listing.beds > 0 && (
                <div className="quick-stat">
                  <span className="quick-stat-value">{listing.beds}</span>
                  <span className="quick-stat-label">Beds</span>
                </div>
              )}
              {listing.baths > 0 && (
                <div className="quick-stat">
                  <span className="quick-stat-value">{formatBaths(listing.baths)}</span>
                  <span className="quick-stat-label">Baths</span>
                </div>
              )}
              {listing.sqft > 0 && (
                <div className="quick-stat">
                  <span className="quick-stat-value">{listing.sqft.toLocaleString()}</span>
                  <span className="quick-stat-label">Sq Ft</span>
                </div>
              )}
              {listing.acres !== undefined && listing.acres > 0 && (
                <div className="quick-stat">
                  <span className="quick-stat-value">{listing.acres.toFixed(2)}</span>
                  <span className="quick-stat-label">Acres</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Property Details Grid */}
      <section className="section section-bg">
        <div className="container">
          <div className="property-details-grid">
            {/* Left Column - Description and Features */}
            <div className="property-main-content">
              {/* Description */}
              {listing.description && (
                <div className="property-section">
                  <h3>About This Property</h3>
                  <p className="property-description">{listing.description}</p>
                </div>
              )}

              {/* Basic Information */}
              <div className="property-section">
                <h3>Basic Information</h3>
                <div className="property-facts">
                  {listing.propStatus && (
                    <div className="property-fact">
                      <span className="fact-label">Status</span>
                      <span className="fact-value">{listing.propStatus}</span>
                    </div>
                  )}
                  {listing.propType && (
                    <div className="property-fact">
                      <span className="fact-label">Property Type</span>
                      <span className="fact-value">{listing.propType}</span>
                    </div>
                  )}
                  {listing.propSubType && (
                    <div className="property-fact">
                      <span className="fact-label">Sub Type</span>
                      <span className="fact-value">{listing.propSubType}</span>
                    </div>
                  )}
                  {listing.beds > 0 && (
                    <div className="property-fact">
                      <span className="fact-label">Bedrooms</span>
                      <span className="fact-value">{listing.beds}</span>
                    </div>
                  )}
                  {listing.baths > 0 && (
                    <div className="property-fact">
                      <span className="fact-label">Total Bathrooms</span>
                      <span className="fact-value">{formatBaths(listing.baths)}</span>
                    </div>
                  )}
                  {listing.fullBaths !== undefined && listing.fullBaths > 0 && (
                    <div className="property-fact">
                      <span className="fact-label">Full Baths</span>
                      <span className="fact-value">{listing.fullBaths}</span>
                    </div>
                  )}
                  {listing.partialBaths !== undefined && listing.partialBaths > 0 && (
                    <div className="property-fact">
                      <span className="fact-label">Half Baths</span>
                      <span className="fact-value">{listing.partialBaths}</span>
                    </div>
                  )}
                  {listing.sqft > 0 && (
                    <div className="property-fact">
                      <span className="fact-label">Square Feet</span>
                      <span className="fact-value">{listing.sqft.toLocaleString()}</span>
                    </div>
                  )}
                  {listing.pricePerSqFt !== undefined && listing.pricePerSqFt > 0 && (
                    <div className="property-fact">
                      <span className="fact-label">Price/Sq Ft</span>
                      <span className="fact-value">${listing.pricePerSqFt.toFixed(2)}</span>
                    </div>
                  )}
                  {listing.acres !== undefined && listing.acres > 0 && (
                    <div className="property-fact">
                      <span className="fact-label">Acres</span>
                      <span className="fact-value">{listing.acres.toFixed(2)}</span>
                    </div>
                  )}
                  {listing.lotSqFt !== undefined && listing.lotSqFt > 0 && (
                    <div className="property-fact">
                      <span className="fact-label">Lot Sq Ft</span>
                      <span className="fact-value">{listing.lotSqFt.toLocaleString()}</span>
                    </div>
                  )}
                  {listing.yearBuilt !== undefined && listing.yearBuilt > 0 && (
                    <div className="property-fact">
                      <span className="fact-label">Year Built</span>
                      <span className="fact-value">{listing.yearBuilt}</span>
                    </div>
                  )}
                  {listing.subdivision && (
                    <div className="property-fact">
                      <span className="fact-label">Subdivision</span>
                      <span className="fact-value">{listing.subdivision}</span>
                    </div>
                  )}
                  {listing.county && (
                    <div className="property-fact">
                      <span className="fact-label">County</span>
                      <span className="fact-value">{listing.county}</span>
                    </div>
                  )}
                  {listing.zoning && (
                    <div className="property-fact">
                      <span className="fact-label">Zoning</span>
                      <span className="fact-value">{listing.zoning}</span>
                    </div>
                  )}
                  {listing.mlsNumber && (
                    <div className="property-fact">
                      <span className="fact-label">MLS #</span>
                      <span className="fact-value">{listing.mlsNumber}</span>
                    </div>
                  )}
                  {listing.daysOnMarket !== undefined && listing.daysOnMarket >= 0 && (
                    <div className="property-fact">
                      <span className="fact-label">Days on Market</span>
                      <span className="fact-value">{listing.daysOnMarket}</span>
                    </div>
                  )}
                  {listing.soldDate && (
                    <div className="property-fact">
                      <span className="fact-label">Sold Date</span>
                      <span className="fact-value">{new Date(listing.soldDate).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Interior */}
              {(listing.appliances?.length || listing.flooringType?.length || listing.hasBasement !== undefined ||
                listing.hasFireplace !== undefined || listing.laundryType?.length || listing.roomList?.length) && (
                <div className="property-section">
                  <h3>Interior</h3>
                  <div className="property-facts">
                    {listing.appliances && listing.appliances.length > 0 && (
                      <div className="property-fact property-fact-wide">
                        <span className="fact-label">Appliances</span>
                        <span className="fact-value">{listing.appliances.join(', ')}</span>
                      </div>
                    )}
                    {listing.flooringType && listing.flooringType.length > 0 && (
                      <div className="property-fact">
                        <span className="fact-label">Flooring</span>
                        <span className="fact-value">{listing.flooringType.join(', ')}</span>
                      </div>
                    )}
                    {listing.hasBasement !== undefined && (
                      <div className="property-fact">
                        <span className="fact-label">Basement</span>
                        <span className="fact-value">{listing.hasBasement ? 'Yes' : 'No'}</span>
                      </div>
                    )}
                    {listing.basementType && listing.basementType.length > 0 && (
                      <div className="property-fact">
                        <span className="fact-label">Basement Type</span>
                        <span className="fact-value">{listing.basementType.join(', ')}</span>
                      </div>
                    )}
                    {listing.hasFireplace !== undefined && (
                      <div className="property-fact">
                        <span className="fact-label">Fireplace</span>
                        <span className="fact-value">{listing.hasFireplace ? 'Yes' : 'No'}</span>
                      </div>
                    )}
                    {listing.fireplaces !== undefined && listing.fireplaces > 0 && (
                      <div className="property-fact">
                        <span className="fact-label">Fireplaces Count</span>
                        <span className="fact-value">{listing.fireplaces}</span>
                      </div>
                    )}
                    {listing.laundryType && listing.laundryType.length > 0 && (
                      <div className="property-fact">
                        <span className="fact-label">Laundry</span>
                        <span className="fact-value">{listing.laundryType.join(', ')}</span>
                      </div>
                    )}
                    {listing.levelsCount && listing.levelsCount.length > 0 && (
                      <div className="property-fact">
                        <span className="fact-label">Levels</span>
                        <span className="fact-value">{listing.levelsCount.join(', ')}</span>
                      </div>
                    )}
                    {listing.roomList && listing.roomList.length > 0 && (
                      <div className="property-fact property-fact-wide">
                        <span className="fact-label">Rooms</span>
                        <span className="fact-value">{listing.roomList.join(', ')}</span>
                      </div>
                    )}
                    {listing.hasCentralAir !== undefined && (
                      <div className="property-fact">
                        <span className="fact-label">Central Air</span>
                        <span className="fact-value">{listing.hasCentralAir ? 'Yes' : 'No'}</span>
                      </div>
                    )}
                    {listing.coolingType && listing.coolingType.length > 0 && (
                      <div className="property-fact">
                        <span className="fact-label">Cooling Type</span>
                        <span className="fact-value">{listing.coolingType.join(', ')}</span>
                      </div>
                    )}
                    {listing.hasHeating !== undefined && (
                      <div className="property-fact">
                        <span className="fact-label">Heating</span>
                        <span className="fact-value">{listing.hasHeating ? 'Yes' : 'No'}</span>
                      </div>
                    )}
                    {listing.heatingType && listing.heatingType.length > 0 && (
                      <div className="property-fact">
                        <span className="fact-label">Heating Type</span>
                        <span className="fact-value">{listing.heatingType.join(', ')}</span>
                      </div>
                    )}
                    {listing.heatingFuel && listing.heatingFuel.length > 0 && (
                      <div className="property-fact">
                        <span className="fact-label">Heating Fuel</span>
                        <span className="fact-value">{listing.heatingFuel.join(', ')}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Exterior */}
              {(listing.architecturalStyle?.length || listing.constructionMaterials?.length || listing.roof?.length ||
                listing.foundationDetails?.length || listing.fencing?.length || listing.pool?.length ||
                listing.outdoorLivingStructures?.length || listing.typeOfParking?.length || listing.hasGarage !== undefined) && (
                <div className="property-section">
                  <h3>Exterior</h3>
                  <div className="property-facts">
                    {listing.architecturalStyle && listing.architecturalStyle.length > 0 && (
                      <div className="property-fact">
                        <span className="fact-label">Architectural Style</span>
                        <span className="fact-value">{listing.architecturalStyle.join(', ')}</span>
                      </div>
                    )}
                    {listing.constructionMaterials && listing.constructionMaterials.length > 0 && (
                      <div className="property-fact">
                        <span className="fact-label">Construction</span>
                        <span className="fact-value">{listing.constructionMaterials.join(', ')}</span>
                      </div>
                    )}
                    {listing.foundationDetails && listing.foundationDetails.length > 0 && (
                      <div className="property-fact">
                        <span className="fact-label">Foundation</span>
                        <span className="fact-value">{listing.foundationDetails.join(', ')}</span>
                      </div>
                    )}
                    {listing.roof && listing.roof.length > 0 && (
                      <div className="property-fact">
                        <span className="fact-label">Roof</span>
                        <span className="fact-value">{listing.roof.join(', ')}</span>
                      </div>
                    )}
                    {listing.hasGarage !== undefined && (
                      <div className="property-fact">
                        <span className="fact-label">Garage</span>
                        <span className="fact-value">{listing.hasGarage ? 'Yes' : 'No'}</span>
                      </div>
                    )}
                    {listing.totalParkingSpaces !== undefined && listing.totalParkingSpaces > 0 && (
                      <div className="property-fact">
                        <span className="fact-label">Parking Spaces</span>
                        <span className="fact-value">{listing.totalParkingSpaces}</span>
                      </div>
                    )}
                    {listing.typeOfParking && listing.typeOfParking.length > 0 && (
                      <div className="property-fact">
                        <span className="fact-label">Parking Type</span>
                        <span className="fact-value">{listing.typeOfParking.join(', ')}</span>
                      </div>
                    )}
                    {listing.parkingFeatures && listing.parkingFeatures.length > 0 && (
                      <div className="property-fact">
                        <span className="fact-label">Parking Features</span>
                        <span className="fact-value">{listing.parkingFeatures.join(', ')}</span>
                      </div>
                    )}
                    {listing.pool && listing.pool.length > 0 && listing.pool[0] !== 'No Pool' && (
                      <div className="property-fact">
                        <span className="fact-label">Pool</span>
                        <span className="fact-value">{listing.pool.join(', ')}</span>
                      </div>
                    )}
                    {listing.fencing && listing.fencing.length > 0 && (
                      <div className="property-fact">
                        <span className="fact-label">Fencing</span>
                        <span className="fact-value">{listing.fencing.join(', ')}</span>
                      </div>
                    )}
                    {listing.outdoorLivingStructures && listing.outdoorLivingStructures.length > 0 && (
                      <div className="property-fact">
                        <span className="fact-label">Outdoor Living</span>
                        <span className="fact-value">{listing.outdoorLivingStructures.join(', ')}</span>
                      </div>
                    )}
                    {listing.lotDimensions && (
                      <div className="property-fact">
                        <span className="fact-label">Lot Dimensions</span>
                        <span className="fact-value">{listing.lotDimensions}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Utilities */}
              {(listing.waterSource?.length || listing.sewerSeptic?.length || listing.electricService?.length ||
                listing.hotWater?.length || listing.utilities?.length || listing.internetServices?.length) && (
                <div className="property-section">
                  <h3>Utilities</h3>
                  <div className="property-facts">
                    {listing.waterSource && listing.waterSource.length > 0 && (
                      <div className="property-fact">
                        <span className="fact-label">Water Source</span>
                        <span className="fact-value">{listing.waterSource.join(', ')}</span>
                      </div>
                    )}
                    {listing.sewerSeptic && listing.sewerSeptic.length > 0 && (
                      <div className="property-fact">
                        <span className="fact-label">Sewer/Septic</span>
                        <span className="fact-value">{listing.sewerSeptic.join(', ')}</span>
                      </div>
                    )}
                    {listing.electricService && listing.electricService.length > 0 && (
                      <div className="property-fact">
                        <span className="fact-label">Electric Service</span>
                        <span className="fact-value">{listing.electricService.join(', ')}</span>
                      </div>
                    )}
                    {listing.hotWater && listing.hotWater.length > 0 && (
                      <div className="property-fact">
                        <span className="fact-label">Hot Water</span>
                        <span className="fact-value">{listing.hotWater.join(', ')}</span>
                      </div>
                    )}
                    {listing.utilities && listing.utilities.length > 0 && (
                      <div className="property-fact">
                        <span className="fact-label">Utilities</span>
                        <span className="fact-value">{listing.utilities.join(', ')}</span>
                      </div>
                    )}
                    {listing.internetServices && listing.internetServices.length > 0 && (
                      <div className="property-fact">
                        <span className="fact-label">Internet</span>
                        <span className="fact-value">{listing.internetServices.join(', ')}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Location & Schools */}
              {(listing.schoolDistrict || listing.highSchool || listing.middleSchool || listing.elementarySchool ||
                listing.municipality || listing.mlsArea) && (
                <div className="property-section">
                  <h3>Location & Schools</h3>
                  <div className="property-facts">
                    {listing.municipality && (
                      <div className="property-fact">
                        <span className="fact-label">Municipality</span>
                        <span className="fact-value">{listing.municipality}</span>
                      </div>
                    )}
                    {listing.mlsArea && (
                      <div className="property-fact">
                        <span className="fact-label">MLS Area</span>
                        <span className="fact-value">{listing.mlsArea}</span>
                      </div>
                    )}
                    {listing.schoolDistrict && (
                      <div className="property-fact">
                        <span className="fact-label">School District</span>
                        <span className="fact-value">{listing.schoolDistrict}</span>
                      </div>
                    )}
                    {listing.elementarySchool && (
                      <div className="property-fact">
                        <span className="fact-label">Elementary School</span>
                        <span className="fact-value">{listing.elementarySchool}</span>
                      </div>
                    )}
                    {listing.middleSchool && (
                      <div className="property-fact">
                        <span className="fact-label">Middle School</span>
                        <span className="fact-value">{listing.middleSchool}</span>
                      </div>
                    )}
                    {listing.highSchool && (
                      <div className="property-fact">
                        <span className="fact-label">High School</span>
                        <span className="fact-value">{listing.highSchool}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* HOA */}
              {(listing.hasHOA !== undefined || listing.hoaFee !== undefined) && (
                <div className="property-section">
                  <h3>HOA Information</h3>
                  <div className="property-facts">
                    {listing.hasHOA !== undefined && (
                      <div className="property-fact">
                        <span className="fact-label">HOA</span>
                        <span className="fact-value">{listing.hasHOA ? 'Yes' : 'No'}</span>
                      </div>
                    )}
                    {listing.hoaName && (
                      <div className="property-fact">
                        <span className="fact-label">HOA Name</span>
                        <span className="fact-value">{listing.hoaName}</span>
                      </div>
                    )}
                    {listing.hoaFee !== undefined && listing.hoaFee > 0 && (
                      <div className="property-fact">
                        <span className="fact-label">HOA Fee</span>
                        <span className="fact-value">${listing.hoaFee.toLocaleString()}{listing.hoaFeeFrequency ? ` / ${listing.hoaFeeFrequency}` : ''}</span>
                      </div>
                    )}
                    {listing.hoaIncludes && listing.hoaIncludes.length > 0 && (
                      <div className="property-fact property-fact-wide">
                        <span className="fact-label">HOA Includes</span>
                        <span className="fact-value">{listing.hoaIncludes.join(', ')}</span>
                      </div>
                    )}
                    {listing.capitalContributionFee !== undefined && listing.capitalContributionFee > 0 && (
                      <div className="property-fact">
                        <span className="fact-label">Capital Contribution</span>
                        <span className="fact-value">${listing.capitalContributionFee.toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Financial / Tax Info */}
              {(listing.taxAnnualAmount !== undefined || listing.taxAssessedValue !== undefined ||
                listing.acceptableFinancing?.length || listing.inclusions || listing.exclusions) && (
                <div className="property-section">
                  <h3>Financial Information</h3>
                  <div className="property-facts">
                    {listing.taxAnnualAmount !== undefined && listing.taxAnnualAmount > 0 && (
                      <div className="property-fact">
                        <span className="fact-label">Annual Taxes</span>
                        <span className="fact-value">${listing.taxAnnualAmount.toLocaleString()}</span>
                      </div>
                    )}
                    {listing.taxAssessedValue !== undefined && listing.taxAssessedValue > 0 && (
                      <div className="property-fact">
                        <span className="fact-label">Tax Assessed Value</span>
                        <span className="fact-value">${listing.taxAssessedValue.toLocaleString()}</span>
                      </div>
                    )}
                    {listing.taxYear !== undefined && listing.taxYear > 0 && (
                      <div className="property-fact">
                        <span className="fact-label">Tax Year</span>
                        <span className="fact-value">{listing.taxYear}</span>
                      </div>
                    )}
                    {listing.acceptableFinancing && listing.acceptableFinancing.length > 0 && (
                      <div className="property-fact">
                        <span className="fact-label">Financing</span>
                        <span className="fact-value">{listing.acceptableFinancing.join(', ')}</span>
                      </div>
                    )}
                    {listing.possession && listing.possession.length > 0 && (
                      <div className="property-fact">
                        <span className="fact-label">Possession</span>
                        <span className="fact-value">{listing.possession.join(', ')}</span>
                      </div>
                    )}
                    {listing.inclusions && (
                      <div className="property-fact property-fact-wide">
                        <span className="fact-label">Inclusions</span>
                        <span className="fact-value">{listing.inclusions}</span>
                      </div>
                    )}
                    {listing.exclusions && (
                      <div className="property-fact property-fact-wide">
                        <span className="fact-label">Exclusions</span>
                        <span className="fact-value">{listing.exclusions}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Features */}
              {listing.features && Object.keys(listing.features).length > 0 && (
                <div className="property-section">
                  <h3>Additional Features</h3>
                  <div className="property-features">
                    {Object.entries(listing.features).map(([category, items]) => {
                      if (!items || items.length === 0) return null;
                      const formattedCategory = category.charAt(0).toUpperCase() + category.slice(1);
                      return (
                        <div key={category} className="feature-category">
                          <h4>{formattedCategory}</h4>
                          <ul>
                            {items.map((item, idx) => (
                              <li key={idx}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Contact Card & Listing Agent */}
            <div className="property-sidebar" ref={sidebarRef}>
              {/* Listing Agent Card */}
              {(listing.listingAgent?.name || listing.listingAgentName) && (
                <div className="contact-card" style={{ marginBottom: '1.5rem' }}>
                  <h3>Listing Agent</h3>
                  <div style={{ marginBottom: '1rem' }}>
                    <p style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{listing.listingAgent?.name || listing.listingAgentName}</p>
                    {(listing.listingOffice?.name || listing.listingOfficeName) && (
                      <p style={{ fontSize: '0.9rem', color: 'var(--color-gray-600)', marginBottom: '0.5rem' }}>{listing.listingOffice?.name || listing.listingOfficeName}</p>
                    )}
                    {(listing.listingAgent?.phone || listing.listingAgentPhone) && (
                      <p style={{ fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                        <a href={`tel:${(listing.listingAgent?.phone || listing.listingAgentPhone || '').replace(/\D/g, '')}`} style={{ color: 'var(--color-gold)' }}>
                          {listing.listingAgent?.phone || listing.listingAgentPhone}
                        </a>
                      </p>
                    )}
                    {(listing.listingAgent?.email || listing.listingAgentEmail) && (
                      <p style={{ fontSize: '0.9rem' }}>
                        <a href={`mailto:${listing.listingAgent?.email || listing.listingAgentEmail}`} style={{ color: 'var(--color-gold)' }}>
                          {listing.listingAgent?.email || listing.listingAgentEmail}
                        </a>
                      </p>
                    )}
                  </div>
                  {listing.coListingAgent?.name && (
                    <div style={{ borderTop: '1px solid var(--color-gray-200)', paddingTop: '0.75rem', marginTop: '0.75rem' }}>
                      <p style={{ fontSize: '0.85rem', color: 'var(--color-gray-500)', marginBottom: '0.25rem' }}>Co-Listing Agent</p>
                      <p style={{ fontWeight: 500 }}>{listing.coListingAgent.name}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Contact Card */}
              <div className="contact-card">
                <h3>Interested in this property?</h3>
                <p>Contact us today to schedule a showing or learn more about this listing.</p>
                <Link to="/contact" className="btn btn-primary" style={{ width: '100%', marginBottom: '1rem' }}>
                  Contact Us
                </Link>
                <a href="tel:+16109485739" className="btn btn-secondary" style={{ width: '100%' }}>
                  <span className="material-icons" style={{ fontSize: '1rem', marginRight: '0.5rem' }}>phone</span>
                  Call (610) 948-5739
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Back to Listings */}
      <section style={{ padding: '4rem 0' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <Link to="/listings" className="btn btn-outline">
            <span className="material-icons" style={{ fontSize: '1rem', marginRight: '0.5rem' }}>arrow_back</span>
            Back to All Listings
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PropertyDetail;
